/**
 * The units a height may be **edited** in. It is always **stored** in `cm`.
 *
 * `cm` first because that is the stored unit and the one the platform reasons
 * in; `ftIn` is a presentation of it, and it is a PAIR of numbers rather than
 * one, which is why this is not a `DurationUnit`-style scale where every unit is
 * a ratio away from the base.
 */
export const HEIGHT_UNITS = ['cm', 'ftIn'] as const;

export type HeightUnit = (typeof HEIGHT_UNITS)[number];

/**
 * The platform's own floor and ceiling, mirroring `answerValueSchema`'s
 * `heightSchema` — which clamps every staff-configured bound into them, so a
 * control offering more would draw a range the api refuses.
 */
export const MIN_HEIGHT_CM = 50;
export const MAX_HEIGHT_CM = 300;

const CM_PER_INCH = 2.54;
const INCHES_PER_FOOT = 12;

const UNIT_SET: ReadonlySet<string> = new Set<string>(HEIGHT_UNITS);

/** Narrows a plain string. Fail-closed — an unknown unit has no conversion. */
export function isHeightUnit(value: string): value is HeightUnit {
  return UNIT_SET.has(value);
}

export interface FeetInches {
  feet: number;
  inches: number;
}

/**
 * A stored centimetre height, as the feet and inches a reader would say.
 *
 * Rounding happens **once, to the inch, before dividing**. Dividing first and
 * rounding the remainder produces `0ft 12in` for anything within half an inch of
 * a foot — a pair no one writes and no input should ever show.
 */
export function toFeetInches(cm: number): FeetInches {
  const totalInches = Math.round(cm / CM_PER_INCH);

  return {
    feet: Math.floor(totalInches / INCHES_PER_FOOT),
    inches: totalInches % INCHES_PER_FOOT,
  };
}

/**
 * A typed feet/inches pair as the whole centimetre that will be stored.
 *
 * An out-of-range inch count is **normalised, not refused**: "5ft 13in" is a
 * legible intention, and the display recomputed from the result says 6ft 1in
 * back — which teaches the convention instead of rejecting the operator.
 *
 * `undefined` only when **neither** component was given: an empty pair is not a
 * height, while "6 feet" with the inches box empty plainly is six feet.
 */
export function fromFeetInches(
  feet: number | undefined,
  inches: number | undefined,
): number | undefined {
  if (feet === undefined && inches === undefined) return undefined;

  return Math.round(((feet ?? 0) * INCHES_PER_FOOT + (inches ?? 0)) * CM_PER_INCH);
}
